import React from "react";
import { motion } from "framer-motion";
import { BENTO, RECORDS, MIGRATION } from "@/constants/testIds";

const tiles = [
  {
    id: 0,
    title: "Freedom Fighters",
    caption: "Families who marched, printed pamphlets, went to jail",
    kicker: "Featured Collection",
    span: "md:col-span-6 lg:col-span-4 lg:row-span-2",
    height: "h-[420px] lg:h-[640px]",
    image: "https://images.unsplash.com/photo-1643474003587-8bbf4bbc01d9?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    id: 1,
    title: "Village Heritage Archives",
    caption: "Old temples, wells, banyan trees, and the elders who remember",
    kicker: "Living Archive",
    span: "md:col-span-6 lg:col-span-4",
    height: "h-[300px]",
    image: "https://images.unsplash.com/photo-1719339285288-0790b8867c6e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    id: 2,
    title: "Diaspora Stories",
    caption: "Nairobi, Fiji, Yorkshire, New Jersey — home carried in a suitcase",
    kicker: "Global Indians",
    span: "md:col-span-6 lg:col-span-4",
    height: "h-[300px]",
    image: "https://images.unsplash.com/photo-1732303105237-f5b5f7a2390e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    id: 3,
    title: "Historic Family Homes",
    caption: "Wadas, tharavads, havelis and courtyards",
    kicker: "Architecture",
    span: "md:col-span-6 lg:col-span-4",
    height: "h-[300px]",
    image: "https://images.unsplash.com/photo-1705837863332-7162639852d8?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    id: 4,
    title: "Traditional Occupations",
    caption: "Weavers, priests, potters, seafarers — a nation's trades",
    kicker: "Livelihoods",
    span: "md:col-span-6 lg:col-span-4",
    height: "h-[300px]",
    image: "https://images.pexels.com/photos/30320070/pexels-photo-30320070.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
];

const records = [
  "Census records", "Land records", "Temple records", "Church records",
  "Mosque records", "Birth records", "Marriage records", "Death records",
  "School records", "Military records", "Court records", "Newspapers",
  "Gazetteers", "Passenger manifests",
];

const migrations = [
  { from: "Village", to: "City", note: "Palakkad → Bombay, 1956" },
  { from: "India", to: "United Kingdom", note: "Punjab → Southall, 1967" },
  { from: "India", to: "East Africa", note: "Gujarat → Nairobi, 1898" },
  { from: "India", to: "United States", note: "Andhra → New Jersey, 1985" },
  { from: "India", to: "Canada", note: "Punjab → Brampton, 1974" },
  { from: "India", to: "Australia", note: "Kerala → Melbourne, 2003" },
];

export function HeritageBento() {
  return (
    <section
      id="collections"
      data-testid={BENTO.root}
      className="relative py-28 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-7">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">Featured collections</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[0.95]">
              A curated
              <span className="italic text-tulsi"> archive </span>
              of Indian life.
            </h2>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-12 gap-6">
          {tiles.map((t, i) => (
            <motion.a
              key={t.id}
              href="#final-cta"
              data-testid={BENTO.tile(i)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative col-span-12 ${t.span} overflow-hidden rounded-2xl border border-ivory-300 bg-ivory`}
            >
              <div className={`relative overflow-hidden ${t.height}`}>
                <img
                  src={t.image}
                  alt={t.title}
                  className="w-full h-full object-cover grayscale-[0.15] sepia-[0.1] group-hover:scale-105 group-hover:grayscale-0 transition-transform duration-[900ms] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-marigold">{t.kicker}</div>
                  <div className="mt-1 font-serif text-2xl md:text-3xl text-ivory tracking-tight leading-tight">
                    {t.title}
                  </div>
                  <div className="mt-1 text-sm text-ivory/70 max-w-md">{t.caption}</div>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Migration() {
  return (
    <section
      data-testid={MIGRATION.root}
      className="relative py-28 md:py-40 bg-ink text-ivory overflow-hidden"
    >
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-5">
          <span className="text-xs uppercase tracking-[0.3em] text-marigold">Migration stories</span>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            One family,
            <span className="italic text-marigold"> many </span>
            shores.
          </h2>
          <p className="mt-6 text-ivory/60 text-lg font-light max-w-md leading-relaxed">
            An interactive timeline that follows your family across generations and continents —
            from a coastal village to a foreign winter, and the letters in between.
          </p>
        </div>
        <div className="col-span-12 md:col-span-7 md:pl-16">
          <ul className="space-y-6">
            {migrations.map((m, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-12 gap-4 items-baseline border-b border-ivory/10 pb-6"
              >
                <div className="col-span-4 font-serif text-2xl md:text-3xl tracking-tight">
                  {m.from}
                </div>
                <div className="col-span-1 text-center text-ivory/40">→</div>
                <div className="col-span-7 flex items-baseline justify-between gap-4">
                  <div className="font-serif italic text-2xl md:text-3xl text-marigold">
                    {m.to}
                  </div>
                  <div className="text-xs uppercase tracking-[0.24em] text-ivory/50">
                    {m.note}
                  </div>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export function HistoricalRecords() {
  return (
    <section
      id="records"
      data-testid={RECORDS.root}
      className="relative py-28 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 items-start">
        <div className="col-span-12 md:col-span-5">
          <span className="text-xs uppercase tracking-[0.3em] text-saffron">Historical records</span>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[0.95]">
            The paper trail
            <span className="italic text-tulsi"> your family </span>
            left behind.
          </h2>
          <p className="mt-6 text-ink-50 text-lg font-light max-w-md leading-relaxed">
            Search census, land, temple, church and mosque records; birth, marriage, death, school
            and military archives; court files, gazetteers and old newspapers.
          </p>
        </div>
        <div className="col-span-12 md:col-span-7 md:pl-16">
          <div className="flex flex-wrap gap-3">
            {records.map((r, i) => (
              <motion.span
                key={r}
                data-testid={RECORDS.chip(i)}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                className="px-4 py-2 rounded-full border border-ivory-300 text-ink text-sm bg-ivory hover:border-tulsi hover:text-tulsi transition cursor-default"
              >
                {r}
              </motion.span>
            ))}
          </div>
          <div className="mt-10 border border-ivory-300 rounded-2xl p-6 bg-ivory-100/60 grid grid-cols-3 gap-6">
            <div>
              <div className="font-serif text-3xl text-ink">1857</div>
              <div className="text-xs text-ink-50 mt-1 uppercase tracking-[0.22em]">Oldest census</div>
            </div>
            <div>
              <div className="font-serif text-3xl text-ink">42k+</div>
              <div className="text-xs text-ink-50 mt-1 uppercase tracking-[0.22em]">Temple entries</div>
            </div>
            <div>
              <div className="font-serif text-3xl text-ink">18</div>
              <div className="text-xs text-ink-50 mt-1 uppercase tracking-[0.22em]">Languages</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
